import { useState } from 'react'

import {
  useLazyListGroupsQuery,
  useGetOneGroupQuery, // this one is the for initial load, which happens before the ui renders anything
  useUpsertGroupMutation,
  useBulkUpdateGroupsMutation,
  useDeleteGroupMutation
} from '../apiSlice/groupApi'

import useSearchCallback from '../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../sharedcomponents/5templates/detailCards/BucketConfig'
import { RootObjectTypes } from '../../common/globalNotary'
import { useNavigate } from 'react-router-dom'

const useGroup = () => {
  const [loadGroups] = useLazyListGroupsQuery()
  const [bulkUpdateGroupsCallBack] = useBulkUpdateGroupsMutation()
  const [upsertGroupRecordCallback] = useUpsertGroupMutation()
  const [deleteGroup] = useDeleteGroupMutation()
  const navigate = useNavigate()

  const {
    searchDatazonesCallback,
    searchGroupsCallback,
    searchPartiesCallback,
    searchFeaturesCallback,
    searchCountryStateCityCallback
  } = useSearchCallback()

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)

  const constructAndSetFinalListBucket = (
    completeResponse,
    primaryGroupRecord
  ) => {
    const groupType = primaryGroupRecord.type
    const finalListBuckets = {}
    finalListBuckets.primero = [
      new BucketConfig(
        completeResponse[0].data,
        searchPartiesCallback,
        'Group Owner',
        null,
        'genericSelect',
        'user'
      )
    ]
    switch (groupType) {
      case 'user':
        finalListBuckets.segundo = [
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'User Group',
            { type: 'user' }
          )
        ]
        finalListBuckets.tercero = [
          new BucketConfig(
            completeResponse[2].data,
            searchPartiesCallback,
            'User',
            null,
            'genericSelect',
            { type: 'user' }
          )
        ]
        break
      case 'operational':
        finalListBuckets.segundo = [
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'Feature Group',
            { type: 'feature' }
          ),
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'Territory Group',
            { type: 'territory' }
          )
        ]
        finalListBuckets.tercero = [
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'User Group',
            { type: 'user' }
          ),
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'Operational Group',
            { type: 'operational' }
          )
        ]
        break
      case 'feature':
        finalListBuckets.segundo = [
          new BucketConfig(
            completeResponse[1].data,
            searchGroupsCallback,
            'Feature Group',
            { type: 'feature' }
          )
        ]
        finalListBuckets.tercero = [
          new BucketConfig(
            completeResponse[2].data,
            searchFeaturesCallback,
            'Feature Set',
            null,
            'hierarchicalSelect'
          )
        ]
        break
      case 'territory':
        finalListBuckets.segundo = [
          new BucketConfig(
            completeResponse[1].data ? completeResponse[1].data : [],
            searchGroupsCallback,
            'Territory Group',
            { type: 'territory' }
          )
        ]

        //Spliting the geo / commercial zones from the same array so they're displayed in the correct positions in the UI
        var geoArray = []
        var comArray = []

        for (var i = 0; i < completeResponse[3].data.length; i++)
        {
          if (completeResponse[3].data[i].type === 'geo')
          {
            geoArray.push(completeResponse[3].data[i])
          }         
          else
          {
            comArray.push(completeResponse[3].data[i])
          }
        }

        comArray['listPropertyName'] = "items"
        geoArray['listPropertyName'] = "items"

        finalListBuckets.tercero = [
          new BucketConfig(
            completeResponse[2].data ? completeResponse[2].data : [],
            searchCountryStateCityCallback,
            'Territory Set',
            null,
            'addressSelect'
          ),
          new BucketConfig(
            comArray,
            searchDatazonesCallback,
            'Commercial Zones',
            { type: 'commercial' }
          ),
          new BucketConfig(
            geoArray,
            searchDatazonesCallback,
            'Geo Zones',
            { type: 'geo' }
          )
        ]
        break
      default:
        throw new Error(`unknow group type: ${groupType}`)
    }
    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const groupTypes = ['user', 'operational', 'feature', 'territory']

  const {
    constructDatazonesByIdArrayCall,
    constructFeaturesByIdArrayCall,
    constructPartiesByIdArrayCall,
    constructGroupsByIdArrayCall,
    constructValueAsPromiseCall
  } = useFetchAuxiliaryLists()

  const fetchAuxiliaryRecords = async primaryGroupRecord => {
    const fetchCallArray = [
      constructPartiesByIdArrayCall(primaryGroupRecord, 'owner_ids'),
      constructGroupsByIdArrayCall(primaryGroupRecord, 'inherit_ids')
    ]
    switch (primaryGroupRecord.type) {
      case 'user':
        fetchCallArray.push(
          constructPartiesByIdArrayCall(primaryGroupRecord, 'items')
        )
        break
      case 'operational': // operaitonal groups has nothing else to fetch
        break
      case 'feature':
        fetchCallArray.push(
          constructFeaturesByIdArrayCall(primaryGroupRecord, 'items')
        )
        break
      case 'territory':
        fetchCallArray.push(
          constructValueAsPromiseCall(
            'meta.addresses',
            primaryGroupRecord.meta?.addresses
          )
        )

        fetchCallArray.push(
          constructDatazonesByIdArrayCall(primaryGroupRecord, 'items')
        )
      
        break
      default:
        throw new Error(`unknown group type: ${primaryGroupRecord.type}`)
    }
    Promise.all(fetchCallArray.map(f => f.call())).then(res => {
      const errors = res?.flatMap(r => r.data?.errors ?? [])
      errors.length === 0
        ? constructAndSetFinalListBucket(res, primaryGroupRecord)
        : console.log('The following errors have occurred: ' +
        errors.map(e => e.msg).join(", "))
    })
  }

  const typeIdToDisplayName = type => {
    switch (type) {
      case 'user':
        return 'User Group'
      case 'feature':
        return 'Feature Group'
      case 'operational':
        return 'Operational Group'
      case 'territory':
        return 'Territory Group'
      default:
        return ''
    }
  }
  const makeInMemoryGroupRecord = type => {
    const meta = {}
    if (type === 'user') 
    { 
      meta.email = null
    }
    //if (type === 'territory') meta.addresses = []
    if (type === 'territory') meta.addresses = []

    return {
      name: '',
      description: '',
      is_system: false,
      inherit_ids: [],
      items: [],
      owner_ids: [],
      type,
      active: true,
      meta
    }
  }

  const upsertGroupCallback = primaryRecord => {
    const returnPromise = new Promise((resolve, reject) => {
      upsertGroupRecordCallback(primaryRecord)
        .unwrap()
        .then(primaryResult => {
          console.log('save response')
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          if (primaryResult.data.errors)
          {
            console.log(primaryResult.data.errors)
          }
          resolve({ id, type: RootObjectTypes.GROUP })
        })
        .catch(e => {
          reject(e)
        })
    })
    return returnPromise
  }

  const removeGroup = async id => {
    deleteGroup({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete account. Please check that other objects do not depend on it.')
    })
  }

  const suspendGroup = async (id, active) => {
    bulkUpdateGroupsCallBack([
      { id, suspended_at: active ? null : new Date() }
    ])
  }

  return {
    typeIdToDisplayName,
    loadGroups,
    bulkUpdateGroupsCallBack,
    useGetOneGroupQuery,
    fetchAuxiliaryRecords,
    makeInMemoryGroupRecord,
    groupTypes,
    finalAuxiliaryRecords,
    upsertGroupCallback,
    removeGroup,
    suspendGroup
  }
}

export default useGroup
